if (!customElements.get('cart-drawer-recommend')) {
  customElements.define(
    'cart-drawer-recommend',
    class CartDrawerRecommend extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.initSwiper();
      }

      disconnectedCallback() {
        if (this.swiper) {
          this.swiper.destroy(true, true);
          this.swiper = null;
        }
        const ids = (this.dataset.modalIds || '').split(',').map((id) => id.trim()).filter(Boolean);
        ids.forEach((id) => {
          const modal = document.getElementById(id);
          if (modal && modal.tagName === 'QUICK-ADD-MODAL' && modal.parentElement === document.body) {
            modal.remove();
          }
        });
      }

      initSwiper() {
        const container = this.querySelector('.cart-drawer-recommend__swiper');
        const nav = this.querySelector('.cart-drawer-recommend__nav');
        const prevEl = this.querySelector('.cart-drawer-recommend__nav-btn--prev');
        const nextEl = this.querySelector('.cart-drawer-recommend__nav-btn--next');
        const slideCount = container ? container.querySelectorAll('.swiper-slide').length : 0;
        if (!container || slideCount < 2 || this.swiper) return;

        if (nav && slideCount > 2) {
          nav.hidden = false;
        }

        const start = () => {
          if (this.swiper || typeof Swiper === 'undefined') return;
          this.swiper = new Swiper(container, {
            slidesPerView: 2,
            spaceBetween: 16,
            observer: true,
            observeParents: true,
            watchOverflow: true,
            navigation: prevEl && nextEl ? { prevEl, nextEl } : undefined,
          });
        };

        if (typeof Swiper !== 'undefined') {
          start();
          return;
        }

        const timer = setInterval(() => {
          if (typeof Swiper === 'undefined') return;
          clearInterval(timer);
          start();
        }, 50);
      }
    }
  );
}

if (!customElements.get('cart-drawer-discount')) {
  customElements.define(
    'cart-drawer-discount',
    class CartDrawerDiscount extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;
        this.form = this.querySelector('[data-discount-form]');
        this.input = this.querySelector('[data-discount-input]');
        this.button = this.querySelector('[data-discount-apply]');
        this.error = this.querySelector('[data-discount-error]');
        this.busy = false;

        if (this.form) {
          this.form.addEventListener('submit', (event) => {
            event.preventDefault();
            this.applyDiscount();
          });
        }

        this.addEventListener('click', (event) => {
          const removeButton = event.target.closest('[data-discount-remove]');
          if (!removeButton || !this.contains(removeButton)) return;
          event.preventDefault();
          this.removeDiscount(removeButton.dataset.discountCode || '');
        });

        // Liquid often omits discount codes after refresh; hydrate from Ajax cart.
        this.hydrateCodesFromCart();
      }

      hydrateCodesFromCart() {
        const cartUrl = (window.routes && window.routes.cart_url) || '/cart';
        fetch(`${cartUrl}.js`, { credentials: 'same-origin' })
          .then((response) => response.json())
          .then((cart) => {
            this.syncCodesFromCart(cart);
            if (typeof cart.total_price !== 'undefined') {
              this.dataset.totalPrice = String(cart.total_price);
            }
            if (typeof cart.total_discount !== 'undefined') {
              this.dataset.totalDiscount = String(cart.total_discount);
            }
          })
          .catch(() => {});
      }

      extractCodesFromCart(parsed) {
        const entries = parsed?.discount_codes || [];
        const applicableCodes = entries
          .filter((entry) => entry?.code && entry.applicable !== false)
          .map((entry) => String(entry.code).trim())
          .filter(Boolean);

        if (applicableCodes.length) return this.uniqueCodes(applicableCodes);

        // Shopify may mark working codes as applicable:false; still show them when
        // the cart has discount savings, so refresh keeps removable pills.
        if (Number(parsed?.total_discount || 0) > 0) {
          const listedCodes = entries
            .map((entry) => (entry?.code ? String(entry.code).trim() : ''))
            .filter(Boolean);
          if (listedCodes.length) return this.uniqueCodes(listedCodes);

          // Fallback: discount titles on line items / cart (often equal the code).
          const titles = [];
          (parsed.items || []).forEach((item) => {
            (item.discounts || []).forEach((discount) => {
              const title = discount?.title ? String(discount.title).trim() : '';
              if (title) titles.push(title);
            });
          });
          (parsed.cart_level_discount_applications || []).forEach((discount) => {
            const title = discount?.title ? String(discount.title).trim() : '';
            if (title) titles.push(title);
          });
          return this.uniqueCodes(titles);
        }

        return [];
      }

      uniqueCodes(codes) {
        const seen = new Set();
        return (codes || []).filter((code) => {
          const key = code.toLowerCase();
          if (!code || seen.has(key)) return false;
          seen.add(key);
          return true;
        });
      }

      syncCodesFromCart(parsed) {
        const listCodes = this.extractCodesFromCart(parsed);
        let list = this.querySelector('[data-discount-codes]');

        if (!listCodes.length) {
          // Keep Liquid-rendered pills when Ajax payload has no explicit codes.
          if (this.getAppliedCodes().length > 0) {
            if (list) list.hidden = false;
            return;
          }
          if (list) {
            list.innerHTML = '';
            list.hidden = true;
          }
          return;
        }

        if (!list) {
          list = document.createElement('ul');
          list.className = 'cart-drawer-discount__codes list-unstyled';
          list.setAttribute('role', 'list');
          list.setAttribute('data-discount-codes', '');
          const errorEl = this.querySelector('[data-discount-error]');
          if (errorEl) errorEl.after(list);
          else this.append(list);
        }

        list.hidden = false;
        list.innerHTML = '';

        const removeLabelTemplate = this.dataset.removeLabel || 'Remove discount code CODE_PLACEHOLDER';

        listCodes.forEach((code) => {
          const item = document.createElement('li');
          item.className = 'cart-drawer-discount__code';

          const icon = document.createElement('span');
          icon.className = 'cart-drawer-discount__code-icon';
          icon.setAttribute('aria-hidden', 'true');
          icon.innerHTML =
            '<svg class="icon icon-discount" viewBox="0 0 12 12"><path fill="currentColor" fill-rule="evenodd" d="M7 0h3a2 2 0 0 1 2 2v3a1 1 0 0 1-.3.7l-6 6a1 1 0 0 1-1.4 0l-4-4a1 1 0 0 1 0-1.4l6-6A1 1 0 0 1 7 0m2 2a1 1 0 1 0 2 0 1 1 0 0 0-2 0" clip-rule="evenodd"/></svg>';

          const text = document.createElement('span');
          text.className = 'cart-drawer-discount__code-text';
          text.textContent = code;

          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'cart-drawer-discount__code-remove';
          button.setAttribute('data-discount-remove', '');
          button.dataset.discountCode = code;
          button.setAttribute(
            'aria-label',
            removeLabelTemplate.replace('CODE_PLACEHOLDER', code).replace('{{ code }}', code)
          );
          button.innerHTML =
            '<span class="svg-wrapper" aria-hidden="true"><svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 12 13" width="10" height="10"><path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" d="M8.486 9.33 2.828 3.67M2.885 9.385l5.544-5.77"/></svg></span>';

          item.append(icon, text, button);
          list.append(item);
        });
      }

      setError(message) {
        if (!this.error) return;
        if (message) {
          this.error.textContent = message;
          this.error.hidden = false;
        } else {
          this.error.textContent = '';
          this.error.hidden = true;
        }
      }

      getAppliedCodes() {
        return Array.from(this.querySelectorAll('[data-discount-remove]'))
          .map((button) => (button.dataset.discountCode || '').trim())
          .filter(Boolean);
      }

      setBusy(isBusy) {
        this.busy = isBusy;
        if (isBusy) {
          this.button?.setAttribute('aria-disabled', 'true');
          this.button?.classList.add('loading');
          this.querySelectorAll('[data-discount-remove]').forEach((button) => {
            button.setAttribute('aria-disabled', 'true');
            button.disabled = true;
          });
        } else {
          this.button?.removeAttribute('aria-disabled');
          this.button?.classList.remove('loading');
          this.querySelectorAll('[data-discount-remove]').forEach((button) => {
            button.removeAttribute('aria-disabled');
            button.disabled = false;
          });
        }
      }

      didDiscountTakeEffect(parsed) {
        const previousPrice = Number(this.dataset.totalPrice || 0);
        const previousDiscount = Number(this.dataset.totalDiscount || 0);
        const nextPrice = Number(parsed?.total_price || 0);
        const nextDiscount = Number(parsed?.total_discount || 0);

        return nextDiscount > previousDiscount || (previousPrice > 0 && nextPrice < previousPrice);
      }

      findDiscountCodeEntry(discountCodes, code) {
        const target = (code || '').trim().toLowerCase();
        if (!target) return null;
        return (discountCodes || []).find(
          (entry) => entry?.code && String(entry.code).trim().toLowerCase() === target
        );
      }

      updateDiscountCodes(codes, { expectedCode, requireApplicable = false } = {}) {
        const discountValue = Array.isArray(codes) ? codes.filter(Boolean).join(',') : '';
        const body = JSON.stringify({
          discount: discountValue,
          sections: ['cart-drawer', 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        });

        return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body })
          .then((response) => response.json())
          .then((parsed) => {
            if (parsed.status) {
              this.setError(parsed.description || parsed.message || this.dataset.error);
              return null;
            }

            if (requireApplicable && expectedCode) {
              const applied = this.findDiscountCodeEntry(parsed.discount_codes, expectedCode);

              // Shopify may omit the code or mark applicable:false even when the
              // discount successfully reduced the cart total — trust money changes.
              const tookEffect = this.didDiscountTakeEffect(parsed);
              const codeOnCartWithSavings =
                Boolean(applied) && Number(parsed?.total_discount || 0) > 0;
              const accepted = tookEffect || codeOnCartWithSavings || (applied && applied.applicable !== false);

              if (!accepted) {
                this.setError(this.dataset.invalid || this.dataset.error);
                const remaining = this.getAppliedCodes().filter(
                  (code) => code.toLowerCase() !== expectedCode.toLowerCase()
                );
                return this.updateDiscountCodes(remaining).then(() => null);
              }
            }

            return parsed;
          });
      }

      renderCart(parsed) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && parsed?.sections) {
          cartDrawer.renderContents(parsed);
        } else {
          this.refreshDrawer();
        }

        // Section HTML may omit discount code pills; sync from Ajax cart payload.
        requestAnimationFrame(() => {
          const discountEl = document.querySelector('cart-drawer-discount');
          if (discountEl && typeof discountEl.syncCodesFromCart === 'function') {
            discountEl.syncCodesFromCart(parsed);
          }
        });
      }

      applyDiscount() {
        const code = (this.input?.value || '').trim();
        if (!code) {
          this.input?.focus();
          return;
        }

        this.setError('');
        if (this.busy) return;

        const existing = this.getAppliedCodes();
        const alreadyApplied = existing.some((entry) => entry.toLowerCase() === code.toLowerCase());
        if (alreadyApplied) {
          this.input.value = '';
          return;
        }

        this.setBusy(true);
        const nextCodes = [...existing, code];

        this.updateDiscountCodes(nextCodes, { expectedCode: code, requireApplicable: true })
          .then((parsed) => {
            if (!parsed) return;
            if (this.input) this.input.value = '';
            this.renderCart(parsed);
          })
          .catch(() => {
            this.setError(this.dataset.error);
          })
          .finally(() => {
            this.setBusy(false);
          });
      }

      removeDiscount(code) {
        const target = (code || '').trim();
        if (!target || this.busy) return;

        this.setError('');
        this.setBusy(true);

        const remaining = this.getAppliedCodes().filter(
          (entry) => entry.toLowerCase() !== target.toLowerCase()
        );

        this.updateDiscountCodes(remaining)
          .then((parsed) => {
            if (!parsed) return;
            this.renderCart(parsed);
          })
          .catch(() => {
            this.setError(this.dataset.error);
          })
          .finally(() => {
            this.setBusy(false);
          });
      }

      refreshDrawer() {
        fetch(`${routes.cart_url}?section_id=cart-drawer`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const newDrawer = html.querySelector('#CartDrawer');
            const current = document.getElementById('CartDrawer');
            if (newDrawer && current) current.innerHTML = newDrawer.innerHTML;
          });
      }
    }
  );
}
